/**
 * 🗺️ Route Configuration System
 *
 * Route hierarchy ve kategorileri tanımlar.
 * Breadcrumb navigation tree'sini bu config'e göre oluşturur.
 */

import {
  commercePaths,
  modelsPaths,
  moduleRegistry,
  type CommerceParams,
  type ModuleInstance,
} from "@/modules";

export enum RouteType {
  /** Ana ekranlar (Sidebar routes) - History'ye eklenir */
  PRIMARY = "primary",
  /** Alt ekranlar (Overlay routes) - Mevcut context'i korur */
  SECONDARY = "secondary",
  /** Modal/Layer routes - Breadcrumb'da gösterilmez */
  LAYER = "layer",
}

export interface RouteConfig {
  path: string;
  type: RouteType;
  i18nKey: string; // i18n key for default label
  icon?: string;
  /**
   * Parent route path (optional)
   * Eğer belirtilirse, breadcrumb'da parent > current şeklinde gösterilir
   */
  parent?: string;
  /**
   * Context provider (optional)
   * Bu route bir context sağlayıcıysa (örn: conversation), alt route'lar bu context'i kullanır
   */
  providesContext?: string;
  /**
   * Context consumer (optional)
   * Bu route bir context kullanıcısıysa (örn: wizard), parent context'i breadcrumb'da gösterir
   */
  consumesContext?: string;
}

// Modül path'leri registry'den: ana domainde /models/library, subdomain'de /library
const MODELS_ROOT = modelsPaths.root();
const MODELS_EXPLORE = modelsPaths.explore();
const LIBRARY = modelsPaths.library();
const WIZARD = modelsPaths.wizardBase();

/**
 * Bir commerce instance'ının (fashion, technology…) breadcrumb girdileri.
 * Kök `/fashion` modül adıyla görünür; subdomain'de kök "/" olduğu için
 * home girdisiyle çakışmaz, atlanır. Gardırop yalnızca özelliği açıksa.
 */
const commerceRouteConfig = (
  instance: ModuleInstance<CommerceParams>,
): Record<string, RouteConfig> => {
  const paths = commercePaths(instance);
  const root = paths.root();
  const shop = paths.shop();
  const product = paths.product("").replace(/\/$/, "");
  const wardrobeEnabled = instance.params?.features?.wardrobe === true;

  return {
    ...(root !== "/"
      ? {
          [root]: {
            path: root,
            type: RouteType.PRIMARY,
            i18nKey: instance.nameKey,
            icon: "🛍️",
          },
        }
      : {}),
    [shop]: {
      path: shop,
      type: RouteType.PRIMARY,
      i18nKey: "common.shopping",
      icon: "🛍️",
    },
    [product]: {
      path: product,
      type: RouteType.SECONDARY,
      i18nKey: "productDetail.title",
      parent: shop, // Ürün detayı kendi modülünün vitrinine bağlı
    },
    ...(wardrobeEnabled
      ? {
          [paths.wardrobe()]: {
            path: paths.wardrobe(),
            type: RouteType.PRIMARY,
            i18nKey: "wardrobe.title",
            icon: "👔",
          },
          [paths.myWardrobe()]: {
            path: paths.myWardrobe(),
            type: RouteType.PRIMARY,
            i18nKey: "wardrobe.myWardrobe",
            icon: "👔",
          },
        }
      : {}),
  };
};

const COMMERCE_ROUTES: Record<string, RouteConfig> = Object.assign(
  {},
  ...moduleRegistry.instances
    .filter((m) => m.blueprint === "commerce")
    .map((m) => commerceRouteConfig(m as ModuleInstance<CommerceParams>)),
);

/**
 * 📋 Route Configuration Map
 *
 * Tüm route'ların hierarchy ve davranışlarını tanımlar
 */
export const ROUTE_CONFIG: Record<string, RouteConfig> = {
  // ==================== PRIMARY ROUTES ====================
  // Sidebar'daki ana ekranlar - History'ye eklenir

  "/": {
    path: "/",
    type: RouteType.PRIMARY,
    i18nKey: "common.home",
    icon: "🏠",
  },

  [MODELS_ROOT]: {
    path: MODELS_ROOT,
    type: RouteType.PRIMARY,
    i18nKey: "common.aiModels",
    icon: "🤖",
  },

  [MODELS_EXPLORE]: {
    path: MODELS_EXPLORE,
    type: RouteType.SECONDARY,
    i18nKey: "modelsLanding.explore",
    icon: "🧭",
    parent: MODELS_ROOT,
  },

  [LIBRARY]: {
    path: LIBRARY,
    type: RouteType.PRIMARY,
    i18nKey: "common.library",
    icon: "📚",
  },

  // Commerce modülleri (fashion, technology…): kök, vitrin, ürün, gardırop
  ...COMMERCE_ROUTES,

  "/chat": {
    path: "/chat",
    type: RouteType.PRIMARY,
    i18nKey: "common.chats",
    icon: "💬",
    providesContext: "conversation", // Chat sayfası conversation context'i sağlar
  },

  "/settings": {
    path: "/settings/profile",
    type: RouteType.PRIMARY,
    i18nKey: "settings.title",
    icon: "⚙️",
  },
  "/settings/:tab": {
    path: "/settings/:tab",
    type: RouteType.PRIMARY,
    i18nKey: "settings.title",
    icon: "⚙️",
  },

  "/pricing": {
    path: "/pricing",
    type: RouteType.PRIMARY,
    i18nKey: "pricing.plans",
    icon: "💳",
  },

  "/cart": {
    path: "/cart",
    type: RouteType.PRIMARY,
    i18nKey: "cart.title",
    icon: "🛒",
  },

  "/orders": {
    path: "/orders",
    type: RouteType.PRIMARY,
    i18nKey: "common.myAccount",
    icon: "📦",
  },

  "/support": {
    path: "/support",
    type: RouteType.PRIMARY,
    i18nKey: "support.title",
    icon: "🎧",
  },

  "/support/tickets": {
    path: "/support/tickets",
    type: RouteType.SECONDARY,
    i18nKey: "support.tabs.myTickets",
    parent: "/support",
  },

  // ==================== SECONDARY ROUTES ====================
  // Alt ekranlar - Mevcut context'i korur, history'ye eklenmez

  [WIZARD]: {
    path: WIZARD,
    type: RouteType.SECONDARY,
    i18nKey: "common.wizard",
    consumesContext: "conversation", // Wizard, mevcut conversation context'ini kullanır
  },

  "/checkout": {
    path: "/checkout",
    type: RouteType.SECONDARY,
    i18nKey: "checkout.title",
    parent: "/cart",
  },

  "/profile": {
    path: "/profile",
    type: RouteType.LAYER,
    i18nKey: "settings.profile.title",
  },
};

/**
 * Route'un config'ini getirir
 */
export const getRouteConfig = (path: string): RouteConfig | undefined => {
  // Exact match
  if (ROUTE_CONFIG[path]) {
    return ROUTE_CONFIG[path];
  }

  // Query parameters'ları temizle
  const cleanPath = path.split("?")[0];
  if (ROUTE_CONFIG[cleanPath]) {
    return ROUTE_CONFIG[cleanPath];
  }

  // Dynamic routes için en uzun eşleşen prefix'i bul.
  // Örnek: /models/wizard/123 -> /models/wizard, /fashion/product/x -> /fashion/product
  const segments = cleanPath.split("/").filter(Boolean);
  for (let i = segments.length - 1; i >= 1; i -= 1) {
    const prefix = `/${segments.slice(0, i).join("/")}`;
    if (ROUTE_CONFIG[prefix]) {
      return ROUTE_CONFIG[prefix];
    }
  }

  return undefined;
};

/**
 * Route'un default label'ını getirir (i18n key)
 */
export const getRouteLabel = (path: string): string => {
  const config = getRouteConfig(path);
  return config?.i18nKey || "common.home";
};

/**
 * Route'un tipini döndürür
 */
export const getRouteType = (path: string): RouteType => {
  const config = getRouteConfig(path);
  return config?.type ?? RouteType.PRIMARY; // Default: PRIMARY
};

/**
 * Route'un parent'ını döndürür
 */
export const getRouteParent = (path: string): string | undefined => {
  const config = getRouteConfig(path);
  return config?.parent;
};

/**
 * Route'un context sağlayıcısını döndürür
 */
export const getRouteContext = (path: string): string | undefined => {
  const config = getRouteConfig(path);
  return config?.providesContext;
};

/**
 * Route'un context tüketicisini döndürür
 */
export const getRouteContextConsumer = (path: string): string | undefined => {
  const config = getRouteConfig(path);
  return config?.consumesContext;
};

/**
 * Path'in breadcrumb'da gösterilip gösterilmeyeceğini kontrol eder
 */
export const shouldShowInBreadcrumb = (path: string): boolean => {
  const type = getRouteType(path);
  return type !== RouteType.LAYER;
};

/**
 * Primary route mu kontrol eder
 */
export const isPrimaryRoute = (path: string): boolean => {
  return getRouteType(path) === RouteType.PRIMARY;
};

/**
 * Secondary route mu kontrol eder
 */
export const isSecondaryRoute = (path: string): boolean => {
  return getRouteType(path) === RouteType.SECONDARY;
};
